import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import './Addons.scss';

interface AddonVariant {
  id: number;
  name: string;
  description: string;
  price: number;
  stock: number;
  in_stock: boolean;
  image?: string;
}

interface AddonCluster {
  id: number;
  name: string;
  description?: string;
  addons_count?: number;
}

interface Addon {
  id: number;
  name: string;
  description: string;
  price: number;
  price_display: string;
  image?: string;
  image_url?: string;
  stock: number;
  in_stock: boolean;
  has_variants: boolean;
  min_price: number;
  max_price: number;
  addon_type: string;
  hidden: boolean;
  variants: AddonVariant[];
  cluster?: AddonCluster;
  currency?: {
    id: number;
    code: string;
    symbol: string;
  };
}

interface AddonsProps {
  addons: Addon[];
  loading?: boolean;
  error?: string | null;
  groupedByCluster?: Record<string, Addon[]>;
  clusters?: AddonCluster[];
}

const Addons: React.FC<AddonsProps> = ({ 
  addons, 
  loading = false, 
  error = null,
  groupedByCluster,
  clusters
}) => {
  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(new Set());

  if (loading) {
    return (
      <div className="addons-container card">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading add-ons...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="addons-container">
        <div className="alert alert-warning" role="alert">
          <strong>Add-ons Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!addons || addons.length === 0) {
    return null;
  }

  const formatPrice = (addon: Addon) => {
    const symbol = addon.currency?.symbol || '৳';
    if (addon.has_variants) {
      if (addon.min_price === addon.max_price) {
        return `${symbol}${addon.min_price.toFixed(2)}`;
      }
      return `${symbol}${addon.min_price.toFixed(2)} - ${symbol}${addon.max_price.toFixed(2)}`;
    }
    return `${symbol}${addon.price.toFixed(2)}`;
  };

  const toggleCluster = (clusterId: number) => {
    const newExpanded = new Set(expandedClusters);
    if (newExpanded.has(clusterId)) {
      newExpanded.delete(clusterId);
    } else {
      newExpanded.add(clusterId);
    }
    setExpandedClusters(newExpanded);
  };

  // Group addons by cluster
  const groupedAddons: Record<string, Addon[]> = groupedByCluster || {};
  const unclusteredAddons: Addon[] = [];

  // If groupedByCluster is not provided, group manually
  if (!groupedByCluster) {
    addons.forEach(addon => {
      if (!addon.hidden) {
        if (addon.cluster) {
          const clusterName = addon.cluster.name;
          if (!groupedAddons[clusterName]) {
            groupedAddons[clusterName] = [];
          }
          groupedAddons[clusterName].push(addon);
        } else {
          unclusteredAddons.push(addon);
        }
      }
    });
  } else {
    // Extract unclustered addons from grouped data
    Object.keys(groupedAddons).forEach(key => {
      if (key === 'Uncategorized' || !key) {
        unclusteredAddons.push(...groupedAddons[key]);
        delete groupedAddons[key];
      }
    });
  }

  const renderAddonItem = (addon: Addon) => (
    <div 
      key={addon.id} 
      className={`addon-item ${!addon.in_stock ? 'disabled' : ''}`}
      onClick={() => addon.in_stock && (window.location.href = `/addons/${addon.id}`)}
    >
      <div className="addon-image">
        {addon.image_url || addon.image ? (
          <img 
            src={addon.image_url || addon.image} 
            alt={addon.name}
            className="addon-image"
            loading="lazy"
          />
        ) : (
          <div className="no-image">
            <i className="fas fa-image"></i>
          </div>
        )}
        {!addon.in_stock && (
          <div className="sold-out-overlay">
            <span className="sold-out-badge">Sold Out</span>
          </div>
        )}
      </div>
      <div className="addon-content">
        <div className="addon-info">
          <h6 className="addon-title" title={addon.name}>{addon.name}</h6>
          <div className="addon-price">
            {addon.has_variants ? (
              addon.min_price === addon.max_price ? (
                <span className="current-price">{addon.price_display || formatPrice(addon)}</span>
              ) : (
                <>
                  <small className="price-label">From</small>
                  <span className="current-price">{addon.price_display || formatPrice(addon)}</span>
                </>
              )
            ) : (
              <span className="current-price">{addon.price_display || formatPrice(addon)}</span>
            )}
          </div>
          {addon.has_variants && (
            <div className="variants-info">
              <i className="fas fa-info-circle"></i>
              <span>Options available</span>
            </div>
          )}
        </div>
        <div className="addon-arrow">
          <i className="fas fa-chevron-right"></i>
        </div>
      </div>
    </div>
  );

  return (
    <div className="event-content-addons">
      <h3 className="section-title mb-3">
        <i className="fas fa-gift me-2"></i>Add-ons
      </h3>
      
      <div className="addons-clusters">
        {/* Render clustered addons */}
        {Object.keys(groupedAddons).map((clusterName) => {
          const clusterAddons = groupedAddons[clusterName];
          const cluster = clusterAddons[0]?.cluster;
          const clusterId = cluster?.id || 0;
          const isExpanded = expandedClusters.has(clusterId);

          return (
            <div key={clusterName} className="addon-cluster mb-4">
              {cluster && (
                <>
                  <div 
                    className="cluster-header"
                    onClick={() => toggleCluster(clusterId)}
                  >
                    <div className="cluster-title">
                      <div className="cluster-name">
                        <i className="fas fa-building me-2"></i>
                        <span>{cluster.name}</span>
                      </div>
                      {cluster.description && (
                        <div className="cluster-description">
                          <small className="text-muted">{cluster.description}</small>
                        </div>
                      )}
                    </div>
                    <div className="cluster-toggle">
                      <i 
                        className={`fas fa-chevron-down ${isExpanded ? 'rotated' : ''}`}
                        id={`cluster-icon-${clusterId}`}
                      ></i>
                    </div>
                  </div>
                  
                  <Collapse in={isExpanded}>
                    <div className="cluster-addons">
                      <div className="addons-list">
                        {clusterAddons.map(addon => renderAddonItem(addon))}
                      </div>
                    </div>
                  </Collapse>
                </>
              )}
            </div>
          );
        })}

        {/* Render unclustered addons */}
        {unclusteredAddons.length > 0 && (
          <div className="addons-list">
            {unclusteredAddons.map(addon => renderAddonItem(addon))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Addons;
